import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, CheckCircle, CalendarDays, Clock } from "lucide-react";
import { format, parseISO } from "date-fns";
import { de, enUS } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingPage() {
  const { t, language } = useLanguage();
  const dateLocale = language === "de" ? de : enUS;
  
  const [studies, setStudies] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [selectedStudy, setSelectedStudy] = useState("");
  const [surname, setSurname] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [childAge, setChildAge] = useState("");
  const [childGender, setChildGender] = useState("");
  const [emailConsent, setEmailConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [bookedAppointment, setBookedAppointment] = useState(null);

  useEffect(() => {
    fetchStudies();
    fetchTimeSlots();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStudies = async () => {
    try {
      const response = await axios.get(`${API}/public/studies`);
      setStudies(response.data);
      if (response.data.length > 0) setSelectedStudy(response.data[0].id);
    } catch (error) {
      toast.error(language === "de" ? "Fehler beim Laden" : "Error loading");
    }
  };

  const fetchTimeSlots = async () => {
    try {
      const response = await axios.get(`${API}/public/time-slots`);
      setTimeSlots(response.data);
    } catch (error) {
      toast.error(language === "de" ? "Fehler beim Laden" : "Error loading");
    }
  };

  const slotsByDate = timeSlots.reduce((acc, slot) => {
    if (!acc[slot.date]) acc[slot.date] = [];
    acc[slot.date].push(slot);
    return acc;
  }, {});

  const sortedDates = Object.keys(slotsByDate).sort();
  const getSelectedSlotDetails = () => timeSlots.find(s => s.id === selectedSlot);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!parentEmail.trim() || !selectedSlot || !selectedStudy) {
      toast.error(t("booking.fillAllFields"));
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/public/appointments`, {
        surname: "",
        parent_email: parentEmail.trim(),
        child_age: 0,
        child_gender: "",
        slot_id: selectedSlot,
        study_id: selectedStudy
      });
      setBookedAppointment(response.data);
      setSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  if (success && bookedAppointment) {
    return (
      <div className="min-h-screen bg-sand-50">
        <Header />
        <main className="px-6 md:px-12 py-12 max-w-2xl mx-auto">
          <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-forest-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="h-8 w-8 text-forest-800" />
              </div>
              <h1 className="font-outfit text-2xl font-medium text-forest-900 mb-3">
                {language === "de" ? "Vielen Dank!" : "Thank you!"}
              </h1>
              <p className="text-stone-600 mb-6">
                {language === "de" 
                  ? "Ihr Terminwunsch wurde entgegengenommen. Sie erhalten in Kürze eine Bestätigungs-E-Mail." 
                  : "Your appointment request has been received. You will receive a confirmation email shortly."}
              </p>
              <div className="bg-sand-50 rounded-xl p-5 mb-6">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <CalendarDays className="h-4 w-4 text-forest-800" />
                  <span className="font-medium text-forest-800 text-sm">{language === "de" ? "Ihre Termine" : "Your appointments"}</span>
                </div>
                <div className="space-y-2 text-sm">
                  <p><strong>{t("booking.session1")}:</strong> {format(parseISO(bookedAppointment.session1_date), "dd. MMMM yyyy", { locale: dateLocale })} {t("booking.oclock")} {bookedAppointment.session1_time}</p>
                  {bookedAppointment.session2_date && (
                    <p><strong>{t("booking.session2")}:</strong> {format(parseISO(bookedAppointment.session2_date), "dd. MMMM yyyy", { locale: dateLocale })} {t("booking.oclock")} {bookedAppointment.session2_time}</p>
                  )}
                </div>
              </div>
              <p className="text-stone-500 text-sm mb-6">
                {language === "de"
                  ? "Falls Sie Ihre Termine ändern möchten, kontaktieren Sie uns bitte unter: sparkstudie@uni-trier.de"
                  : "If you wish to change your appointments, please contact us at: sparkstudie@uni-trier.de"}
              </p>
              <Link to="/"><Button className="bg-forest-800 hover:bg-forest-900 text-white rounded-full px-6">{t("booking.backToHome")}</Button></Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const selectedSlotDetails = getSelectedSlotDetails();

  return (
    <div className="min-h-screen bg-sand-50">
      <Header />
      <main className="px-6 md:px-12 py-12 max-w-4xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-stone-600 hover:text-forest-800 mb-6 text-sm">
          <ArrowLeft className="h-4 w-4" />{t("booking.back")}
        </Link>
        <h1 className="font-outfit text-3xl font-medium text-forest-900 mb-2">{t("booking.title")}</h1>
        <p className="text-stone-600 mb-8">{t("booking.subtitle")}</p>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Time Slots */}
            <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-outfit text-lg text-forest-800">{t("booking.selectTimeSlot")}</CardTitle>
                <CardDescription className="text-sm">{t("booking.selectTimeSlotDesc")}</CardDescription>
              </CardHeader>
              <CardContent>
                {sortedDates.length === 0 ? (
                  <div className="p-4 bg-terracotta-50 rounded-xl"><p className="text-terracotta-900 text-sm">{t("booking.noSlots")}</p></div>
                ) : (
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {sortedDates.map((date) => (
                      <div key={date} className="border border-stone-200 rounded-xl p-3">
                        <h3 className="font-medium text-forest-900 text-sm mb-2">
                          {format(parseISO(date), "EEEE, dd. MMMM yyyy", { locale: dateLocale })}
                        </h3>
                        <div className="grid grid-cols-2 gap-2">
                          {slotsByDate[date].sort((a, b) => a.time.localeCompare(b.time)).map((slot) => (
                            <button key={slot.id} type="button" onClick={() => setSelectedSlot(slot.id)}
                              className={`p-2 rounded-lg text-xs font-medium transition-all ${selectedSlot === slot.id ? "bg-forest-800 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}>
                              <Clock className="h-3 w-3 inline mr-1" />{slot.time} {t("booking.oclock")}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {selectedSlotDetails && (
                  <div className="mt-3 p-3 bg-forest-50 rounded-xl text-sm">
                    <p className="text-forest-800 font-medium mb-1">{t("booking.selectedSlots")}</p>
                    <p className="text-forest-900">{t("booking.session1")}: {format(parseISO(selectedSlotDetails.date), "dd.MM.yyyy", { locale: dateLocale })} {selectedSlotDetails.time}</p>
                    <p className="text-forest-900">{t("booking.session2")}: {format(parseISO(selectedSlotDetails.session2_date), "dd.MM.yyyy", { locale: dateLocale })} {selectedSlotDetails.session2_time}</p>
                    <p className="text-stone-500 text-xs mt-2 italic">
                      {language === "de"
                        ? "Falls der zweite Termin für Sie nicht passend ist, kontaktieren Sie uns bitte unter: sparkstudie@uni-trier.de"
                        : "If the second appointment is not suitable for you, please contact us at: sparkstudie@uni-trier.de"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Form Fields */}
            <Card className="bg-white rounded-2xl border-stone-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="font-outfit text-lg text-forest-800">{t("booking.yourData")}</CardTitle>
                <CardDescription className="text-sm">{t("booking.fillAllFields")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1">
                  <Label className="text-sm">{t("booking.email")}</Label>
                  <Input type="email" placeholder={t("booking.emailPlaceholder")} value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} className="bg-stone-50 border-stone-200 rounded-xl" data-testid="email-input" />
                </div>
                <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-xl">
                  <Checkbox id="email-consent" checked={emailConsent} onCheckedChange={setEmailConsent} className="mt-0.5" data-testid="email-consent-checkbox" />
                  <label htmlFor="email-consent" className="text-xs text-stone-600 leading-relaxed cursor-pointer">
                    {language === "de"
                      ? "Ich bin damit einverstanden, dass meine E-Mail-Adresse zur Terminbestätigung und Kommunikation im Rahmen der Studie verwendet wird."
                      : "I agree that my email address may be used for appointment confirmation and communication within the study."}
                  </label>
                </div>
                <Button type="submit" disabled={loading || !selectedSlot || !parentEmail.trim() || !selectedStudy || !emailConsent}
                  className="w-full bg-forest-800 hover:bg-forest-900 text-white rounded-full py-5 transition-transform hover:-translate-y-0.5 disabled:opacity-50" data-testid="submit-booking-btn">
                  {loading ? t("booking.booking") : t("booking.bookAppointment")}
                </Button>
              </CardContent>
            </Card>
          </div>
        </form>
      </main>
    </div>
  );
}
